'use client';
import { useRef, useState, useEffect, useCallback } from 'react';
import { motion, useInView, AnimatePresence } from 'motion/react';
import {
  Building,
  Cog,
  Calculator,
  FileText,
  ClipboardCheck,
  ChevronLeft,
  ChevronRight,
  Clock,
  Pause,
  Play,
} from 'lucide-react';

const phases = [
  {
    id: 1,
    title: 'Organisation Setup',
    description:
      'Set your currency, identify your industry, and name the site being assessed.',
    icon: Building,
    duration: '5 mins',
    steps: ['Select currency', 'Choose industry', 'Name your site'],
    color: 'amber',
  },
  {
    id: 2,
    title: 'Business Functions',
    description:
      'Define what your facility does and the financial value of each function.',
    icon: Cog,
    duration: '10 mins',
    steps: [
      'Select core functions',
      'Set throughput values',
      'Define SLA penalties',
    ],
    color: 'slate',
  },
  {
    id: 3,
    title: 'Systems Inventory',
    description:
      'Identify which systems support each function and their dependencies.',
    icon: ClipboardCheck,
    duration: '10 mins',
    steps: [
      'Select systems from templates',
      'Map to functions',
      'Define dependencies',
    ],
    color: 'teal',
  },
  {
    id: 4,
    title: 'Impact Assessment',
    description:
      'For each system, assess recovery times, resilience, and financial impact.',
    icon: Calculator,
    duration: '15 mins',
    steps: [
      'Set MTD and RTO',
      'Assess resilience',
      'Calculate exposure',
    ],
    color: 'amber',
  },
  {
    id: 5,
    title: 'Generate Reports',
    description:
      'Review your dashboard and generate board-ready executive reports.',
    icon: FileText,
    duration: '5 mins',
    steps: [
      'Review findings',
      'Select report types',
      'Download outputs',
    ],
    color: 'slate',
  },
];

const AUTO_TRANSITION_DURATION = 6000; // 6 seconds

const colorClasses = {
  amber: {
    bg: 'bg-[hsl(var(--amber))]/10',
    border: 'border-[hsl(var(--amber))]/30',
    text: 'text-[hsl(var(--amber))]',
    iconBg: 'bg-gradient-to-br from-[hsl(var(--amber))] to-[hsl(var(--amber-light))]',
    progress: 'bg-[hsl(var(--amber))]',
  },
  slate: {
    bg: 'bg-[hsl(var(--slate-dark))]/5',
    border: 'border-[hsl(var(--slate-dark))]/20',
    text: 'text-[hsl(var(--slate-dark))]',
    iconBg: 'bg-gradient-to-br from-[hsl(var(--slate-dark))] to-[hsl(var(--slate-medium))]',
    progress: 'bg-[hsl(var(--slate-dark))]',
  },
  teal: {
    bg: 'bg-[hsl(var(--teal))]/10',
    border: 'border-[hsl(var(--teal))]/30',
    text: 'text-[hsl(var(--teal))]',
    iconBg: 'bg-gradient-to-br from-[hsl(var(--teal))] to-[hsl(var(--teal-light))]',
    progress: 'bg-[hsl(var(--teal))]',
  },
};

// Phase preview content components
function PhasePreview({ phase }: { phase: (typeof phases)[0] }) {
  const colors = colorClasses[phase.color as keyof typeof colorClasses];

  const previewContent: Record<number, React.ReactNode> = {
    1: (
      <div className="space-y-6">
        <div>
          <h4 className="text-lg font-serif font-medium text-[hsl(var(--slate-deep))] mb-2">
            Which currency would you like to use?
          </h4>
          <p className="text-sm text-[hsl(var(--slate-medium))]">
            All financial calculations will use this currency
          </p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {['£ GBP', '$ USD', '€ EUR', '¥ JPY'].map((currency, i) => (
            <motion.div
              key={currency}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: i * 0.1 }}
              className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${i === 0
                  ? 'border-[hsl(var(--amber))] bg-[hsl(var(--amber))]/5 shadow-md'
                  : 'border-[hsl(var(--border))] hover:border-[hsl(var(--amber))]/50'
                }`}
            >
              <span className="font-mono font-medium text-[hsl(var(--slate-dark))]">
                {currency}
              </span>
            </motion.div>
          ))}
        </div>
      </div>
    ),
    2: (
      <div className="space-y-6">
        <div>
          <h4 className="text-lg font-serif font-medium text-[hsl(var(--slate-deep))] mb-2">
            What's your typical throughput?
          </h4>
          <p className="text-sm text-[hsl(var(--slate-medium))]">
            For: Production/Assembly
          </p>
        </div>
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <motion.input
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              type="text"
              value="500"
              readOnly
              className="flex-1 px-4 py-3 rounded-xl border border-[hsl(var(--border))] bg-white text-lg font-mono focus:outline-none focus:ring-2 focus:ring-[hsl(var(--amber))]/20"
            />
            <span className="text-[hsl(var(--slate-medium))]">units/hour</span>
          </div>
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="p-4 rounded-xl bg-[hsl(var(--amber))]/5 border border-[hsl(var(--amber))]/20"
          >
            <div className="text-xs font-medium text-[hsl(var(--amber))] mb-1">
              Calculated Impact
            </div>
            <div className="font-mono text-xl font-semibold text-[hsl(var(--slate-deep))]">
              £125,000/hour at risk
            </div>
          </motion.div>
        </div>
      </div>
    ),
    3: (
      <div className="space-y-6">
        <div>
          <h4 className="text-lg font-serif font-medium text-[hsl(var(--slate-deep))] mb-2">
            Which systems do you have?
          </h4>
          <p className="text-sm text-[hsl(var(--slate-medium))]">
            Control Systems
          </p>
        </div>
        <div className="space-y-2">
          {[
            { name: 'SCADA System', checked: true },
            { name: 'PLC Network', checked: true },
            { name: 'HMI Displays', checked: false },
            { name: 'DCS', checked: false },
          ].map((system, i) => (
            <motion.div
              key={system.name}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.1 }}
              className={`flex items-center gap-3 p-3 rounded-lg border transition-all ${system.checked
                  ? 'border-[hsl(var(--teal))]/30 bg-[hsl(var(--teal))]/5'
                  : 'border-[hsl(var(--border))] hover:border-[hsl(var(--teal))]/20'
                }`}
            >
              <div
                className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${system.checked
                    ? 'border-[hsl(var(--teal))] bg-[hsl(var(--teal))]'
                    : 'border-[hsl(var(--border))]'
                  }`}
              >
                {system.checked && (
                  <svg className="w-3 h-3 text-white" viewBox="0 0 12 12">
                    <path
                      fill="currentColor"
                      d="M10.28 2.28L4 8.56 1.72 6.28a.75.75 0 00-1.06 1.06l3 3a.75.75 0 001.06 0l7-7a.75.75 0 00-1.06-1.06z"
                    />
                  </svg>
                )}
              </div>
              <span className="text-[hsl(var(--slate-dark))]">{system.name}</span>
            </motion.div>
          ))}
        </div>
      </div>
    ),
    4: (
      <div className="space-y-6">
        <div>
          <h4 className="text-lg font-serif font-medium text-[hsl(var(--slate-deep))] mb-2">
            How long can you operate without SCADA?
          </h4>
          <p className="text-sm text-[hsl(var(--slate-medium))]">
            Maximum Tolerable Downtime (MTD)
          </p>
        </div>
        <div className="grid grid-cols-2 gap-2">
          {['< 1 hour', '1-4 hours', '4-8 hours', '8-24 hours'].map(
            (option, i) => (
              <motion.div
                key={option}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: i * 0.1 }}
                className={`p-3 rounded-lg border-2 text-center cursor-pointer transition-all ${i === 1
                    ? 'border-[hsl(var(--amber))] bg-[hsl(var(--amber))]/5 shadow-md'
                    : 'border-[hsl(var(--border))] hover:border-[hsl(var(--amber))]/50'
                  }`}
              >
                <span className="text-sm font-medium text-[hsl(var(--slate-dark))]">
                  {option}
                </span>
              </motion.div>
            )
          )}
        </div>
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="p-3 rounded-lg bg-amber-50 border border-amber-200 flex items-start gap-2"
        >
          <span className="text-amber-600 text-sm">⚠️</span>
          <span className="text-sm text-amber-700">
            Gap identified: RTO (6h) exceeds MTD (4h)
          </span>
        </motion.div>
      </div>
    ),
    5: (
      <div className="space-y-6">
        <div>
          <h4 className="text-lg font-serif font-medium text-[hsl(var(--slate-deep))] mb-2">
            Your Assessment Summary
          </h4>
          <p className="text-sm text-[hsl(var(--slate-medium))]">
            Ready to generate reports
          </p>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="p-4 rounded-xl bg-[hsl(var(--amber))]/5 border border-[hsl(var(--amber))]/20"
          >
            <div className="text-xs text-[hsl(var(--amber))] mb-1">
              Total Exposure
            </div>
            <div className="font-mono text-2xl font-bold text-[hsl(var(--slate-deep))]">
              £2.8M
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.1 }}
            className="p-4 rounded-xl bg-[hsl(var(--slate-dark))]/5 border border-[hsl(var(--slate-dark))]/20"
          >
            <div className="text-xs text-[hsl(var(--slate-dark))] mb-1">
              Systems Assessed
            </div>
            <div className="font-mono text-2xl font-bold text-[hsl(var(--slate-deep))]">
              12
            </div>
          </motion.div>
        </div>
        <div className="space-y-2">
          <div className="text-xs font-medium text-[hsl(var(--slate-medium))] uppercase tracking-wider">
            Generate Reports
          </div>
          {['Executive Summary', 'Full BIA Report', 'Priority Matrix'].map(
            (report, i) => (
              <motion.div
                key={report}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 + i * 0.1 }}
                className="flex items-center gap-3 p-3 rounded-lg border border-[hsl(var(--border))] hover:border-[hsl(var(--amber))]/30 cursor-pointer transition-all hover:bg-[hsl(var(--amber))]/5"
              >
                <FileText className="w-4 h-4 text-[hsl(var(--slate-medium))]" />
                <span className="text-sm text-[hsl(var(--slate-dark))]">
                  {report}
                </span>
              </motion.div>
            )
          )}
        </div>
      </div>
    ),
  };

  return previewContent[phase.id] || null;
}

export default function HowItWorks() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const [activePhase, setActivePhase] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [progress, setProgress] = useState(0);
  const progressRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const intervalRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const goToNext = useCallback(() => {
    setActivePhase((prev) => (prev + 1) % phases.length);
    setProgress(0);
  }, []);

  const goToPrev = useCallback(() => {
    setActivePhase((prev) => (prev - 1 + phases.length) % phases.length);
    setProgress(0);
  }, []);

  const goToPhase = useCallback((index: number) => {
    setActivePhase(index);
    setProgress(0);
  }, []);

  // Auto-transition effect
  useEffect(() => {
    if (isPaused || !isInView) return;

    // Progress animation
    const startTime = Date.now();
    progressRef.current = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const newProgress = Math.min((elapsed / AUTO_TRANSITION_DURATION) * 100, 100);
      setProgress(newProgress);
    }, 50);

    // Auto-transition
    intervalRef.current = setTimeout(() => {
      goToNext();
    }, AUTO_TRANSITION_DURATION);

    return () => {
      if (progressRef.current) clearInterval(progressRef.current);
      if (intervalRef.current) clearTimeout(intervalRef.current);
    };
  }, [activePhase, isPaused, isInView, goToNext]);

  const currentPhase = phases[activePhase];
  const colors = colorClasses[currentPhase.color as keyof typeof colorClasses];

  return (
    <section
      ref={ref}
      id="how-it-works"
      className="relative py-24 lg:py-32 bg-[hsl(var(--muted))]/30"
    >
      {/* Subtle background pattern */}
      <div className="absolute inset-0 bg-dot-pattern opacity-30" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[hsl(var(--teal))]/10 border border-[hsl(var(--teal))]/20 text-sm font-medium text-[hsl(var(--teal))] mb-6">
            <ClipboardCheck className="w-4 h-4" />
            Guided Journey
          </span>

          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-medium text-[hsl(var(--slate-deep))] mb-6 text-balance">
            Complete Your Assessment in{' '}
            <span className="text-gradient-amber">30-45 Minutes</span>
          </h2>

          <p className="text-lg text-[hsl(var(--slate-medium))] leading-relaxed">
            Our wizard-based interface guides you through five phases, asking
            one question at a time. Save and return anytime.
          </p>
        </motion.div>

        {/* Main content */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="relative"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          {/* Phase selector - horizontal pills */}
          <div className="flex flex-wrap justify-center gap-2 mb-8">
            {phases.map((phase, index) => {
              const phaseColors = colorClasses[phase.color as keyof typeof colorClasses];
              const isActive = index === activePhase;
              const Icon = phase.icon;

              return (
                <button
                  key={phase.id}
                  onClick={() => goToPhase(index)}
                  className={`relative flex items-center gap-2 px-4 py-2.5 rounded-full transition-all duration-300 ${isActive
                      ? `${phaseColors.bg} ${phaseColors.border} border-2 shadow-lg`
                      : 'bg-white border border-[hsl(var(--border))] hover:border-[hsl(var(--slate-medium))]/30'
                    }`}
                >
                  {/* Progress indicator for active phase */}
                  {isActive && (
                    <div className="absolute inset-0 rounded-full overflow-hidden">
                      <motion.div
                        className={`absolute bottom-0 left-0 h-1 ${phaseColors.progress}`}
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                  )}

                  <div
                    className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold ${isActive
                        ? `${phaseColors.iconBg} text-white`
                        : 'bg-[hsl(var(--muted))] text-[hsl(var(--slate-medium))]'
                      }`}
                  >
                    {phase.id}
                  </div>
                  <span
                    className={`text-sm font-medium hidden sm:inline ${isActive
                        ? 'text-[hsl(var(--slate-deep))]'
                        : 'text-[hsl(var(--slate-medium))]'
                      }`}
                  >
                    {phase.title}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Navigation and pause controls */}
          <div className="flex items-center justify-center gap-4 mb-8">
            <button
              onClick={goToPrev}
              className="p-2 rounded-full bg-white border border-[hsl(var(--border))] hover:border-[hsl(var(--slate-dark))]/30 hover:shadow-md transition-all"
            >
              <ChevronLeft className="w-5 h-5 text-[hsl(var(--slate-dark))]" />
            </button>

            <button
              onClick={() => setIsPaused(!isPaused)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full border transition-all ${isPaused
                  ? 'bg-[hsl(var(--amber))]/10 border-[hsl(var(--amber))]/30 text-[hsl(var(--amber))]'
                  : 'bg-white border-[hsl(var(--border))] text-[hsl(var(--slate-medium))] hover:border-[hsl(var(--slate-dark))]/30'
                }`}
            >
              {isPaused ? (
                <>
                  <Play className="w-4 h-4" />
                  <span className="text-sm font-medium">Resume</span>
                </>
              ) : (
                <>
                  <Pause className="w-4 h-4" />
                  <span className="text-sm font-medium">Pause</span>
                </>
              )}
            </button>

            <button
              onClick={goToNext}
              className="p-2 rounded-full bg-white border border-[hsl(var(--border))] hover:border-[hsl(var(--slate-dark))]/30 hover:shadow-md transition-all"
            >
              <ChevronRight className="w-5 h-5 text-[hsl(var(--slate-dark))]" />
            </button>
          </div>

          {/* Preview card */}
          <div className="max-w-3xl mx-auto">
            <div className="relative bg-white rounded-2xl shadow-2xl border border-[hsl(var(--border))] overflow-hidden">
              {/* Progress bar */}
              <div className="h-1.5 bg-[hsl(var(--muted))]">
                <motion.div
                  className={`h-full ${colors.progress}`}
                  initial={{ width: '0%' }}
                  animate={{ width: `${((activePhase + 1) / phases.length) * 100}%` }}
                  transition={{ duration: 0.5 }}
                />
              </div>

              {/* Header */}
              <div className="px-6 py-4 border-b border-[hsl(var(--border))] bg-[hsl(var(--muted))]/30">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-lg ${colors.iconBg} flex items-center justify-center`}>
                      <currentPhase.icon className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-[hsl(var(--slate-dark))]">
                        Phase {currentPhase.id}: {currentPhase.title}
                      </div>
                      <div className="text-xs text-[hsl(var(--slate-medium))]">
                        {currentPhase.description}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 text-xs text-[hsl(var(--slate-medium))]">
                    <Clock className="w-3.5 h-3.5" />
                    {currentPhase.duration}
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-8 min-h-[400px]">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={activePhase}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.3 }}
                  >
                    <PhasePreview phase={currentPhase} />
                  </motion.div>
                </AnimatePresence>
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-[hsl(var(--border))] bg-[hsl(var(--muted))]/30 flex justify-between items-center">
                <button
                  onClick={goToPrev}
                  className="px-4 py-2 text-sm text-[hsl(var(--slate-medium))] hover:text-[hsl(var(--slate-dark))] transition-colors flex items-center gap-1"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Back
                </button>

                {/* Dot indicators */}
                <div className="flex items-center gap-2">
                  {phases.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => goToPhase(index)}
                      className={`transition-all duration-300 rounded-full ${index === activePhase
                          ? `w-6 h-2 ${colors.progress}`
                          : 'w-2 h-2 bg-[hsl(var(--slate-medium))]/30 hover:bg-[hsl(var(--slate-medium))]/50'
                        }`}
                    />
                  ))}
                </div>

                <button
                  onClick={goToNext}
                  className={`px-6 py-2 text-sm font-medium text-white rounded-lg transition-all ${colors.iconBg} hover:shadow-lg flex items-center gap-1`}
                >
                  {activePhase === phases.length - 1 ? 'Finish' : 'Continue'}
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Total time indicator */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ delay: 0.5 }}
            className="text-center mt-8"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[hsl(var(--border))] shadow-sm">
              <Clock className="w-4 h-4 text-[hsl(var(--amber))]" />
              <span className="text-sm text-[hsl(var(--slate-medium))]">
                Total time:{' '}
                <strong className="text-[hsl(var(--slate-dark))]">
                  30-45 minutes
                </strong>
              </span>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
